/**
 * The live link to the engine (`/ws`, nova/remote/hub.py): Remote Studio's state
 * sync, commands and audio signalling.
 *
 * - **State**: the station window publishes its state (`publishState`); every other
 *   window keeps a copy from `state.snapshot` and numbered `state.patch`es, and asks
 *   for the whole state again (`state.resync`) when a number is missing.
 * - **Clock**: the engine sends its time every 5 s; `serverNow()` is this window's
 *   clock corrected by the difference, so countdowns agree everywhere.
 * - **Messages**: `onMessage(type, fn)` and `send(message)` for commands and WebRTC.
 *
 * It reconnects by itself (1 s, 2 s, 4 s... up to 15 s). Nothing here plays sound or
 * changes anything on its own: what a message means is up to whoever listens.
 */

import { engineBase } from "../api";
import { isDesktop } from "../platform";
import { currentToken as token, onSessionChange } from "./session";

export interface LinkUser {
  id: number;
  username: string;
  displayName: string;
  name?: string;
  isAdmin: boolean;
  permissions: string[];
  network?: boolean;
}

/** A window on the live link (who is connected), as the engine says. */
export interface Presence {
  id: string;
  name: string;
  username: string | null;
  network: boolean;
  studio: boolean;
}

type Message = { type: string; [key: string]: unknown };
type Handler = (m: Message) => void;

interface LinkInfo {
  connected: boolean;
  /** This window's id on the link, once signed in. */
  id: string | null;
  /** Opened over the network (Remote Studio), not on the station computer. */
  network: boolean;
  /** Whether a station window is connected. */
  studio: boolean;
  /** Whether the station is Nova's playout (every window is then a desk of it). */
  playout: boolean;
  user: LinkUser | null;
}

let socket: WebSocket | null = null;
let info: LinkInfo = { connected: false, id: null, network: false, studio: false, playout: false, user: null };
let seq = -1;
let mirror: Record<string, unknown> = {};
let offset = 0;
let retry = 1000;
let wanted = false;
let timer: number | null = null;
const handlers = new Map<string, Set<Handler>>();
const stateListeners = new Set<() => void>();
const infoListeners = new Set<() => void>();
let presenceList: Presence[] = [];

function url(): string {
  const base = engineBase() || window.location.origin;
  const u = new URL("/ws", base.startsWith("http") ? base : window.location.origin);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  const t = token();
  if (t) u.searchParams.set("token", t);
  return u.toString();
}

function setInfo(next: Partial<LinkInfo>) {
  info = { ...info, ...next };
  infoListeners.forEach((fn) => fn());
}

function emit(m: Message) {
  handlers.get(m.type)?.forEach((fn) => fn(m));
  handlers.get("*")?.forEach((fn) => fn(m));
}

function receive(m: Message) {
  switch (m.type) {
    case "hello":
      setInfo({ id: String(m.id), network: !!m.network, studio: !!m.studio, playout: !!m.playout, user: (m.user as LinkUser) ?? null });
      if (typeof m.now === "number") offset = m.now - Date.now();
      // The desktop app runs on the server: it is the studio's screen, not a person on the call.
      if (isDesktop()) send({ type: "screen.studio" });
      break;
    case "clock":
      if (typeof m.now === "number") offset = m.now - Date.now();
      break;
    case "studio":
      setInfo({ studio: !!m.present, playout: !!m.present && !!m.playout });
      break;
    case "presence":
      presenceList = (m.list as Presence[]) ?? [];
      setInfo({ studio: !!m.studio, playout: !!m.playout });
      break;
    case "state.snapshot":
      seq = Number(m.seq);
      mirror = (m.state as Record<string, unknown>) ?? {};
      if (m.studio !== undefined) setInfo({ studio: !!m.studio });
      stateListeners.forEach((fn) => fn());
      break;
    case "state.patch": {
      const n = Number(m.seq);
      if (n !== seq + 1) {
        send({ type: "state.resync" });
        return;
      }
      seq = n;
      const next = { ...mirror, ...((m.set as Record<string, unknown>) ?? {}) };
      for (const k of (m.unset as string[]) ?? []) delete next[k];
      mirror = next;
      stateListeners.forEach((fn) => fn());
      break;
    }
  }
  emit(m);
}

function connect() {
  if (!wanted || socket) return;
  let ws: WebSocket;
  try {
    ws = new WebSocket(url());
  } catch {
    schedule();
    return;
  }
  socket = ws;
  ws.onopen = () => {
    retry = 1000;
    setInfo({ connected: true });
    emit({ type: "link.open" });
  };
  ws.onmessage = (e) => {
    try {
      receive(JSON.parse(String(e.data)) as Message);
    } catch {
      // A message this window does not understand is left alone.
    }
  };
  ws.onclose = () => {
    socket = null;
    seq = -1;
    setInfo({ connected: false, id: null, playout: false });
    emit({ type: "link.closed" });
    schedule();
  };
}

function schedule() {
  if (!wanted || timer !== null) return;
  timer = window.setTimeout(() => {
    timer = null;
    connect();
  }, retry);
  retry = Math.min(15_000, retry * 2);
}

let lastToken: string | null = null;

/** Open the link (once; later calls do nothing). A new sign-in reconnects it as that person. */
export function openLink() {
  if (!wanted) {
    lastToken = token();
    onSessionChange(() => {
      const now = token();
      if (now === lastToken) return;
      lastToken = now;
      socket?.close(); // reconnects with the new token
    });
  }
  wanted = true;
  connect();
}

/** Close it and stop reconnecting (signing out). */
export function closeLink() {
  wanted = false;
  if (timer !== null) window.clearTimeout(timer);
  timer = null;
  socket?.close();
}

export function send(message: Message): boolean {
  if (!socket || socket.readyState !== WebSocket.OPEN) return false;
  socket.send(JSON.stringify(message));
  return true;
}

export function onMessage(type: string, fn: Handler): () => void {
  let set = handlers.get(type);
  if (!set) handlers.set(type, (set = new Set()));
  set.add(fn);
  return () => set!.delete(fn);
}

/** The station's state as last heard (remote windows). */
export const linkState = (): Record<string, unknown> => mirror;
export function onLinkState(fn: () => void): () => void {
  stateListeners.add(fn);
  return () => stateListeners.delete(fn);
}

export const linkInfo = (): LinkInfo => info;
export function onLinkInfo(fn: () => void): () => void {
  infoListeners.add(fn);
  return () => infoListeners.delete(fn);
}
/** Who has a window open on the link (the station window aside). */
export const presence = (): Presence[] => presenceList;

/** The engine's clock (ms), from this window's and the last difference heard. */
export const serverNow = (): number => Date.now() + offset;

/** From the station window: what On air is now (the engine sends windows only what changed). */
export function publishState(state: Record<string, unknown>) {
  send({ type: "state.snapshot", state });
}

let nextId = 1;
const waiting = new Map<string, (r: { ok: boolean; error?: string }) => void>();
onMessage("command.result", (m) => {
  const done = waiting.get(String(m.id));
  waiting.delete(String(m.id));
  done?.({ ok: !!m.ok, error: (m.error as string) || undefined });
});

/** From a remote window: ask the station to do something. Resolves with its answer. */
export function command(action: string, args: unknown[] = []): Promise<{ ok: boolean; error?: string }> {
  const id = `c${nextId++}`;
  return new Promise((resolve) => {
    if (!send({ type: "command", id, action, args })) {
      resolve({ ok: false, error: "Not connected to the station." });
      return;
    }
    waiting.set(id, resolve);
    window.setTimeout(() => {
      if (waiting.delete(id)) resolve({ ok: false, error: "The station did not answer." });
    }, 10_000);
  });
}

/** From the station window: answer a command. */
export function answer(to: string, id: unknown, ok: boolean, error?: string) {
  send({ type: "command.result", to, id, ok, error });
}

/** Whether `user` (as the engine described them) may do something that needs `permission`. */
export function userMay(user: LinkUser | null | undefined, permission: string): boolean {
  if (!user) return true; // accounts off: this computer
  return user.isAdmin || user.permissions.includes(permission);
}
